/**
 * tm/components/StepOwnerFields.tsx — 업무 템플릿 하위업무의 「담당 · 안내 글 · 받을 서류」 (2026-10-28 지도사 「각 단계에 뭐라고 안내할지 내가 입력」)
 *  담당이 임지도사가 아니면 아래 두 칸이 열린다 — 안내 글(대표님 · 팀장님 화면에 그대로) · 받을 서류(이미 받은 것은 화면에서 저절로 빠짐)
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { C, T } from './ui';
import { OWNERS, ownerOf, splitItems } from '../stepOwner';
import type { Owner } from '../stepOwner';

export interface OwnerCfg { owner?: Owner; ask?: string; ask_items?: string[]; judge?: { yes: string; no: string } | null }

/** 줄 안의 담당 고르기 — 하위업무 줄 높이 · 글자 크기에 맞춤 */
export function OwnerSelect({ value, onChange }: { value: Owner | undefined; onChange: (v: Owner) => void }) {
  const v = ownerOf(value);
  const on = v !== 'jido';
  return (
    <select value={v} onChange={e => onChange(ownerOf(e.target.value))} title="누가 할 일인지 — 대표님 · 팀장님이면 이 단계가 되었을 때 「답 기다림」으로 열려요"
      style={{ width: 96, flexShrink: 0, padding: '7px 6px', borderRadius: 6, border: `1px solid ${on ? '#0F766E55' : C.border}`, fontSize: 11, outline: 'none', color: on ? '#0F766E' : '#6B7280', background: on ? '#F0FDFA' : '#fff', fontWeight: 600, cursor: 'pointer', textAlign: 'center', boxSizing: 'border-box' }}>
      {OWNERS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

/** 담당이 대표님 · 팀장님일 때 — 안내 글 · 받을 서류 */
export function OwnerDetail({ cfg, onChange }: { cfg: OwnerCfg; onChange: (patch: OwnerCfg) => void }) {
  const [draft, setDraft] = useState('');
  const owner = ownerOf(cfg.owner);
  if (owner === 'jido') return null;
  const items = cfg.ask_items || [];
  const who = owner === 'fp' ? '팀장님' : owner === 'both' ? '대표님 · 팀장님' : '대표님';
  const add = () => { const next = splitItems([...items, draft].join(',')); onChange({ ask_items: next }); setDraft(''); };
  const lbl: CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 4, display: 'block' };
  return (
    <div style={{ margin: '4px 0 2px 32px', padding: '10px 12px', borderRadius: 8, background: '#F8FAFC', border: `1px solid ${C.border}` }}>
      <label style={lbl}>{who}께 보낼 안내 <span style={{ fontWeight: 400, color: C.muted }}>· 이 단계가 되면 {who} 화면 「지금 하실 일」에 그대로 나가요</span></label>
      <textarea value={cfg.ask || ''} rows={2} maxLength={300} onChange={e => onChange({ ask: e.target.value })} placeholder="예) 조합 통장에 출자금을 넣으신 뒤 아래 서류를 보내 주세요"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, lineHeight: 1.5, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
      <label style={{ ...lbl, marginTop: 8 }}>받을 서류 <span style={{ fontWeight: 400, color: C.muted }}>· 이미 받은 서류(자료 포털 · 벤처 자료수집)는 화면에서 저절로 빠져요</span></label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {items.map(x => (
          <span key={x} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, background: '#fff', border: `1px solid ${C.border}`, fontSize: 12, color: '#1F2937' }}>
            {x}
            <button type="button" aria-label={`${x} 빼기`} onClick={() => onChange({ ask_items: items.filter(y => y !== x) })} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#9CA3AF', fontSize: 12, lineHeight: 1 }}>✕</button>
          </span>
        ))}
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); if (draft.trim()) add(); } }} onBlur={() => { if (draft.trim()) add(); }}
          placeholder={items.length ? '서류 더하기' : '예) 조합통장 잔고증명서 (엔터로 더하기)'} style={{ flex: '1 1 180px', minWidth: 140, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, outline: 'none' }} />
      </div>
    </div>
  );
}
